import React, { useState, useEffect } from 'react';
import { Calendar, Search, Filter, Edit2, Trash2, X as XIcon, CheckCircle, Clock, XCircle as CancelIcon, Plus } from 'lucide-react';
import axios from 'axios';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeader = () => {
  const token = localStorage.getItem('adminToken');
  return { Authorization: `Bearer ${token}` };
};

const BookingsManager = () => {
  const { admin } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [stats, setStats] = useState(null);
  const [locations, setLocations] = useState([]);
  const [editFormData, setEditFormData] = useState({
    status: '',
    paymentStatus: '',
    refundReason: ''
  });

  // Get user role from admin context
  const userRole = admin?.role || 'superadmin';

  useEffect(() => {
    if (userRole === 'reception') {
      // Reception can only see confirmed bookings
      setStatusFilter('confirmed');
    }
    fetchBookings();
    fetchStats();
  }, [statusFilter, userRole]);

  const fetchBookings = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await axios.get(`${API}/api/admin/bookings`, {
        headers: getAuthHeader(),
        params
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/bookings/stats/dashboard`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking || !cancellationReason.trim()) {
      alert('Please provide a cancellation reason');
      return;
    }

    try {
      await axios.post(
        `${API}/api/admin/bookings/${selectedBooking._id}/cancel`,
        {
          cancellationReason,
          cancelledBy: 'Admin'
        },
        { headers: getAuthHeader() }
      );

      alert('Booking cancelled successfully');
      setShowCancelModal(false);
      setCancellationReason('');
      setSelectedBooking(null);
      fetchBookings();
      fetchStats();
    } catch (error) {
      alert('Failed to cancel booking: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/admin/bookings/${bookingId}`, {
        headers: getAuthHeader()
      });
      alert('Booking deleted successfully');
      fetchBookings();
      fetchStats();
    } catch (error) {
      alert('Failed to delete booking: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (booking) => {
    setSelectedBooking(booking);
    setEditFormData({
      status: booking.status,
      paymentStatus: booking.paymentStatus || 'pending',
      refundReason: booking.refundReason || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedBooking) return;

    // Validate refund reason if payment status is refund
    if (editFormData.paymentStatus === 'refund' && !editFormData.refundReason.trim()) {
      alert('Please provide a refund reason');
      return;
    }

    try {
      await axios.patch(
        `${API}/api/admin/bookings/${selectedBooking._id}`,
        editFormData,
        { headers: getAuthHeader() }
      );

      alert('Booking updated successfully');
      setShowEditModal(false);
      setSelectedBooking(null);
      fetchBookings();
      fetchStats();
    } catch (error) {
      console.error('Failed to update booking:', error);
      alert('Failed to update booking: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCreateCustomBooking = async (formData) => {
    try {
      await axios.post(
        `${API}/api/admin/bookings/custom`,
        formData,
        { headers: getAuthHeader() }
      );

      alert('Custom booking created successfully');
      setShowCreateModal(false);
      fetchBookings();
      fetchStats();
    } catch (error) {
      console.error('Failed to create booking:', error);
      alert('Failed to create booking: ' + (error.response?.data?.error || error.message));
    }
  };

  // Fetch locations for custom booking form
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${API}/api/locations`);
        setLocations(response.data);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    };
    fetchLocations();
  }, []);

  const filteredBookings = bookings.filter(booking =>
    booking.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.bookingId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.whatsappNumber.includes(searchTerm)
  );

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-green-500/20 text-green-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      cancelled: 'bg-red-500/20 text-red-400',
      completed: 'bg-blue-500/20 text-blue-400'
    };
    return styles[status] || 'bg-gray-500/20 text-gray-400';
  };

  const getPaymentStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-500/20 text-green-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      partial: 'bg-orange-500/20 text-orange-400',
      refunded: 'bg-purple-500/20 text-purple-400',
      failed: 'bg-red-500/20 text-red-400'
    };
    return styles[status] || 'bg-gray-500/20 text-gray-400';
  };

  if (loading) {
    return <div className="text-white text-center py-12">Loading bookings...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-white mb-2">Bookings Manager</h1>
          <p className="text-gray-400">Manage all bookings and reservations</p>
        </div>
        {activeTab === 'bookings' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-yellow-400 hover:bg-yellow-300 text-black px-6 py-3 rounded-lg font-bold flex items-center space-x-2 transition-colors"
          >
            <Plus size={20} />
            <span>New Booking</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`pb-4 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'bookings'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Bookings
          </button>
          <button
            onClick={() => setActiveTab('drone-sales')}
            className={`pb-4 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'drone-sales'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Drone Sales
          </button>
        </div>
      </div>

      {/* Bookings Tab Content */}
      {activeTab === 'bookings' && (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {userRole !== 'reception' && (
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Total</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
          )}
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
            <p className="text-green-400 text-sm">{userRole === 'reception' ? 'Confirmed (My Location)' : 'Confirmed'}</p>
            <p className="text-2xl font-bold text-green-400">{stats.confirmed}</p>
          </div>
          {userRole !== 'reception' && (
            <>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                <p className="text-yellow-400 text-sm">Pending</p>
                <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="text-red-400 text-sm">Cancelled</p>
                <p className="text-2xl font-bold text-red-400">{stats.cancelled}</p>
              </div>
            </>
          )}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <p className="text-blue-400 text-sm">Today</p>
            <p className="text-2xl font-bold text-blue-400">{stats.todayBookings}</p>
          </div>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
            <p className="text-purple-400 text-sm">{userRole === 'reception' ? 'Location Revenue' : 'Total Revenue'}</p>
            <p className="text-2xl font-bold text-purple-400">₹{stats.totalRevenue}</p>
          </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, email, booking ID, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
              />
            </div>
            {/* Hide status filter for reception - they only see confirmed */}
            {userRole !== 'reception' && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              >
                <option value="all">All Status</option>
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
            )}
          </div>

          {/* Bookings Table */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Booking ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Date & Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Jumpers</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Payment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredBookings.map((booking) => (
                    <tr key={booking._id} className="hover:bg-gray-700/50">
                  <td className="px-4 py-4">
                    <div className="text-white font-medium">{booking.bookingId}</div>
                    {booking.isAgencyBooking && (
                      <span className="text-xs text-purple-400">Agency: {booking.agencyName}</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-white">{booking.customerName}</div>
                    <div className="text-sm text-gray-400">{booking.email}</div>
                    <div className="text-sm text-gray-400">{booking.whatsappNumber}</div>
                  </td>
                  <td className="px-4 py-4 text-white">{booking.locationName}</td>
                  <td className="px-4 py-4">
                    <div className="text-white">{new Date(booking.bookingDate).toLocaleDateString()}</div>
                    <div className="text-sm text-gray-400">{booking.slotTime}</div>
                  </td>
                  <td className="px-4 py-4 text-white text-center">{booking.numberOfJumpers}</td>
                  <td className="px-4 py-4">
                    <div className="text-white font-medium">₹{booking.totalAmount}</div>
                    {booking.discountAmount > 0 && (
                      <div className="text-xs text-green-400">Discount: ₹{booking.discountAmount}</div>
                    )}
                    {booking.pendingAmount > 0 && (
                      <div className="text-xs text-orange-400">Pending: ₹{booking.pendingAmount}</div>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusBadge(booking.status)}`}>
                      {booking.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${getPaymentStatusBadge(booking.paymentStatus)}`}>
                      {booking.paymentStatus}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(booking)}
                        className="p-2 hover:bg-blue-500/20 rounded-lg text-blue-400 transition-colors"
                        title="Edit Booking"
                      >
                        <Edit2 size={16} />
                      </button>
                      {/* Hide cancel and delete for reception */}
                      {userRole !== 'reception' && (
                        <>
                          {booking.status !== 'cancelled' && (
                            <button
                              onClick={() => {
                                setSelectedBooking(booking);
                                setShowCancelModal(true);
                              }}
                              className="p-2 hover:bg-orange-500/20 rounded-lg text-orange-400 transition-colors"
                              title="Cancel Booking"
                            >
                              <CancelIcon size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteBooking(booking._id)}
                            className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors"
                            title="Delete Booking"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          </div>

          {filteredBookings.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="mx-auto mb-4 text-gray-600" size={48} />
              <p className="text-gray-400">No bookings found</p>
            </div>
          )}
        </>
      )}

      {/* Drone Sales Tab Content */}
      {activeTab === 'drone-sales' && (
        <DroneSalesTab locations={locations} />
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Cancel Booking</h2>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellationReason('');
                  setSelectedBooking(null);
                }}
                className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <XIcon size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-gray-400 mb-2">Booking ID: <span className="text-white font-bold">{selectedBooking?.bookingId}</span></p>
                <p className="text-gray-400 mb-2">Customer: <span className="text-white">{selectedBooking?.customerName}</span></p>
              </div>
              <div>
                <label className="text-white font-medium mb-2 block">Cancellation Reason *</label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  placeholder="Enter reason for cancellation..."
                />
              </div>
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancellationReason('');
                    setSelectedBooking(null);
                  }}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleCancelBooking}
                  className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition-colors"
                >
                  Cancel Booking
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-black text-white">Edit Booking</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedBooking(null);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <XIcon size={24} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Booking Details (Read-only) */}
                <div className="bg-gray-700/50 rounded-lg p-4 space-y-2">
                  <p className="text-white"><span className="text-gray-400">Customer:</span> {selectedBooking.customer?.name || 'N/A'}</p>
                  <p className="text-white"><span className="text-gray-400">Location:</span> {selectedBooking.location?.name || 'N/A'}</p>
                  <p className="text-white"><span className="text-gray-400">Date:</span> {new Date(selectedBooking.bookingDate).toLocaleDateString()}</p>
                  <p className="text-white"><span className="text-gray-400">Jumpers:</span> {selectedBooking.numberOfJumpers}</p>
                  <p className="text-white"><span className="text-gray-400">Total:</span> ₹{selectedBooking.totalAmount}</p>
                </div>

                {/* Booking Status */}
                <div>
                  <label className="text-white font-medium mb-2 block">Booking Status *</label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="No Show">No Show</option>
                    <option value="Jumped">Jumped</option>
                    <option value="No Jump">No Jump</option>
                  </select>
                </div>

                {/* Payment Status */}
                <div>
                  <label className="text-white font-medium mb-2 block">Payment Status *</label>
                  <select
                    value={editFormData.paymentStatus}
                    onChange={(e) => setEditFormData({ ...editFormData, paymentStatus: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  >
                    <option value="pending">Pending</option>
                    <option value="initiated">Initiated</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                    <option value="With agent">With Agent</option>
                    <option value="paid">Paid</option>
                    <option value="refund">Refund</option>
                  </select>
                </div>

                {/* Refund Reason (conditional) */}
                {editFormData.paymentStatus === 'refund' && (
                  <div>
                    <label className="text-white font-medium mb-2 block">Refund Reason *</label>
                    <textarea
                      value={editFormData.refundReason}
                      onChange={(e) => setEditFormData({ ...editFormData, refundReason: e.target.value })}
                      rows="3"
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                      placeholder="Enter reason for refund..."
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-4 pt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedBooking(null);
                  }}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Custom Booking Modal */}
      {showCreateModal && (
        <CustomBookingForm
          locations={locations}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateCustomBooking}
        />
      )}
    </div>
  );
};

// Custom Booking Form Component
const CustomBookingForm = ({ locations, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    locationId: '',
    bookingDate: '',
    timeSlot: '',
    numberOfJumpers: 1,
    pricePerJumper: 0,
    status: 'confirmed',
    bookingType: 'walk-in',
    agencyId: '',
    paymentId: '',
    paymentType: 'cash',
    upiDetails: '',
    specialRequests: ''
  });

  const [agencies, setAgencies] = useState([]);

  // Fetch agencies when location changes
  useEffect(() => {
    if (formData.locationId && formData.bookingType === 'agent') {
      fetchAgenciesByLocation(formData.locationId);
    }
  }, [formData.locationId, formData.bookingType]);

  const fetchAgenciesByLocation = async (locationId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/api/admin/agencies?locationId=${locationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencies(response.data);
    } catch (error) {
      console.error('Failed to fetch agencies:', error);
      setAgencies([]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.customerName || !formData.customerPhone || !formData.locationId || !formData.bookingDate) {
      alert('Please fill all required fields');
      return;
    }

    // Validate based on booking type
    if (formData.bookingType === 'agent' && !formData.agencyId) {
      alert('Please select an agency');
      return;
    }

    if (formData.bookingType === 'website' && !formData.paymentId) {
      alert('Please enter Payment ID');
      return;
    }

    if (formData.bookingType === 'walk-in' && formData.paymentType === 'UPI' && !formData.upiDetails) {
      alert('Please specify UPI details');
      return;
    }

    const bookingData = {
      customerName: formData.customerName,
      customerEmail: formData.customerEmail,
      customerPhone: formData.customerPhone,
      locationId: formData.locationId,
      bookingDate: formData.bookingDate,
      timeSlot: formData.timeSlot,
      numberOfJumpers: formData.numberOfJumpers,
      pricePerJumper: formData.pricePerJumper,
      totalAmount: formData.numberOfJumpers * formData.pricePerJumper,
      status: 'confirmed',
      bookingType: formData.bookingType,
      specialRequests: formData.specialRequests
    };

    // Add booking type specific fields
    if (formData.bookingType === 'agent') {
      bookingData.agencyId = formData.agencyId;
      bookingData.isAgencyBooking = true;
    } else if (formData.bookingType === 'website') {
      bookingData.paymentId = formData.paymentId;
      bookingData.paymentStatus = 'paid';
    } else if (formData.bookingType === 'walk-in') {
      bookingData.paymentType = formData.paymentType;
      if (formData.paymentType === 'UPI') {
        bookingData.upiDetails = formData.upiDetails;
      }
      bookingData.paymentStatus = 'paid';
    }

    onSubmit(bookingData);
  };

  const selectedLocation = locations.find(loc => loc._id === formData.locationId);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-black text-white">Create Custom Booking</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <XIcon size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Customer Details */}
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h3 className="text-lg font-bold text-yellow-400 mb-3">Customer Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white font-medium mb-2 block">Customer Name *</label>
                  <input
                    type="text"
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    required
                  />
                </div>
                <div>
                  <label className="text-white font-medium mb-2 block">Phone *</label>
                  <input
                    type="tel"
                    value={formData.customerPhone}
                    onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-white font-medium mb-2 block">Email</label>
                  <input
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h3 className="text-lg font-bold text-yellow-400 mb-3">Booking Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white font-medium mb-2 block">Location *</label>
                  <select
                    value={formData.locationId}
                    onChange={(e) => {
                      const loc = locations.find(l => l._id === e.target.value);
                      setFormData({ 
                        ...formData, 
                        locationId: e.target.value,
                        pricePerJumper: loc?.price || 0
                      });
                    }}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    required
                  >
                    <option value="">Select Location</option>
                    {locations.map(loc => (
                      <option key={loc._id} value={loc._id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-white font-medium mb-2 block">Date *</label>
                  <input
                    type="date"
                    value={formData.bookingDate}
                    onChange={(e) => setFormData({ ...formData, bookingDate: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    required
                  />
                </div>
                <div>
                  <label className="text-white font-medium mb-2 block">Time Slot</label>
                  <input
                    type="time"
                    value={formData.timeSlot}
                    onChange={(e) => setFormData({ ...formData, timeSlot: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                </div>
                <div>
                  <label className="text-white font-medium mb-2 block">Number of Jumpers *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.numberOfJumpers}
                    onChange={(e) => setFormData({ ...formData, numberOfJumpers: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    required
                  />
                </div>
                <div>
                  <label className="text-white font-medium mb-2 block">Price Per Jumper *</label>
                  <input
                    type="number"
                    value={formData.pricePerJumper}
                    onChange={(e) => setFormData({ ...formData, pricePerJumper: parseFloat(e.target.value) })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    required
                  />
                </div>
                <div>
                  <label className="text-white font-medium mb-2 block">Total Amount</label>
                  <input
                    type="text"
                    value={`₹${(formData.numberOfJumpers * formData.pricePerJumper).toLocaleString()}`}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg text-yellow-400 font-bold"
                  />
                </div>
              </div>
            </div>

            {/* Booking Type */}
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h3 className="text-lg font-bold text-yellow-400 mb-3">Booking Type</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-white font-medium mb-2 block">Type *</label>
                  <select
                    value={formData.bookingType}
                    onChange={(e) => setFormData({ ...formData, bookingType: e.target.value, agencyId: '', paymentId: '', paymentType: 'cash', upiDetails: '' })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  >
                    <option value="walk-in">Walk-in</option>
                    <option value="agent">Agent</option>
                    <option value="website">Website</option>
                  </select>
                </div>

                {/* Agent Type - Show Agency Dropdown */}
                {formData.bookingType === 'agent' && (
                  <div>
                    <label className="text-white font-medium mb-2 block">Agency Name *</label>
                    {!formData.locationId ? (
                      <p className="text-sm text-gray-400 italic py-3">Please select a location first</p>
                    ) : (
                      <select
                        value={formData.agencyId}
                        onChange={(e) => setFormData({ ...formData, agencyId: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                        required
                      >
                        <option value="">Select Agency</option>
                        {agencies.map(agency => (
                          <option key={agency._id} value={agency._id}>{agency.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Website Type - Show Payment ID */}
                {formData.bookingType === 'website' && (
                  <div>
                    <label className="text-white font-medium mb-2 block">Payment ID *</label>
                    <input
                      type="text"
                      value={formData.paymentId}
                      onChange={(e) => setFormData({ ...formData, paymentId: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                      placeholder="Enter payment gateway ID"
                      required
                    />
                  </div>
                )}

                {/* Walk-in Type - Show Payment Type */}
                {formData.bookingType === 'walk-in' && (
                  <>
                    <div>
                      <label className="text-white font-medium mb-2 block">Payment Type *</label>
                      <select
                        value={formData.paymentType}
                        onChange={(e) => setFormData({ ...formData, paymentType: e.target.value, upiDetails: '' })}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                      >
                        <option value="cash">Cash</option>
                        <option value="UPI">UPI</option>
                      </select>
                    </div>

                    {/* UPI Details - Show only if UPI selected */}
                    {formData.paymentType === 'UPI' && (
                      <div>
                        <label className="text-white font-medium mb-2 block">UPI Details *</label>
                        <input
                          type="text"
                          value={formData.upiDetails}
                          onChange={(e) => setFormData({ ...formData, upiDetails: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                          placeholder="Enter UPI transaction ID or details"
                          required
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Special Requests */}
            <div>
              <label className="text-white font-medium mb-2 block">Special Requests / Notes</label>
              <textarea
                value={formData.specialRequests}
                onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                rows="3"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                placeholder="Any special requirements or notes..."
              />
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg transition-colors"
              >
                Create Booking
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Drone Sales Tab Component
const DroneSalesTab = ({ locations }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationFilter, setLocationFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    videosSold: 0,
    videoPrice: 0,
    paymentType: 'cash',
    upiDetails: ''
  });

  useEffect(() => {
    fetchDroneSalesBookings();
  }, [locationFilter, dateFilter]);

  const fetchDroneSalesBookings = async () => {
    try {
      const params = new URLSearchParams();
      if (locationFilter !== 'all') params.append('locationId', locationFilter);
      if (dateFilter) params.append('date', dateFilter);

      const response = await axios.get(`${API}/api/admin/bookings/drone-sales?${params}`, {
        headers: getAuthHeader()
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch drone sales bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (booking) => {
    setSelectedBooking(booking);
    setEditData({
      videosSold: booking.droneVideos?.videosSold || 0,
      videoPrice: booking.droneVideos?.videoPrice || 0,
      paymentType: booking.droneVideos?.paymentType || 'cash',
      upiDetails: booking.droneVideos?.upiDetails || ''
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!selectedBooking) return;

    if (editData.videosSold > selectedBooking.numberOfJumpers) {
      alert(`Number of videos cannot exceed number of jumpers (${selectedBooking.numberOfJumpers})`);
      return;
    }

    if (editData.paymentType === 'UPI' && !editData.upiDetails.trim()) {
      alert('Please provide UPI details');
      return;
    }

    try {
      await axios.patch(
        `${API}/api/admin/bookings/${selectedBooking._id}/drone-sales`,
        {
          videosSold: editData.videosSold,
          videoPrice: editData.videoPrice,
          paymentType: editData.paymentType,
          upiDetails: editData.paymentType === 'UPI' ? editData.upiDetails : ''
        },
        { headers: getAuthHeader() }
      );

      alert('Drone sales updated successfully');
      setShowEditModal(false);
      setSelectedBooking(null);
      setEditData({
        videosSold: 0,
        videoPrice: 0,
        paymentType: 'cash',
        upiDetails: ''
      });
      fetchDroneSalesBookings();
    } catch (error) {
      console.error('Failed to update drone sales:', error);
      alert('Failed to update: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleNoSale = async (bookingId) => {
    if (!window.confirm('Mark this booking as "No Sale" for drone videos?')) return;

    try {
      await axios.patch(
        `${API}/api/admin/bookings/${bookingId}/drone-sales`,
        {
          videosSold: 0,
          videoPrice: 0,
          noSale: true
        },
        { headers: getAuthHeader() }
      );

      alert('Marked as No Sale');
      fetchDroneSalesBookings();
    } catch (error) {
      console.error('Failed to mark no sale:', error);
      alert('Failed to update: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return <div className="text-white text-center py-12">Loading drone sales...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <label className="text-white font-medium mb-2 block">Location</label>
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400"
          >
            <option value="all">All Locations</option>
            {locations.map(loc => (
              <option key={loc._id} value={loc._id}>{loc.name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="text-white font-medium mb-2 block">Date</label>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400"
          />
        </div>
      </div>

      {/* Drone Sales Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto overflow-y-auto max-h-[600px]">
          <table className="w-full min-w-max">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Booking ID</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Customer</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Location</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Date</th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Jumpers</th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Videos</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Price</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Total</th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {bookings.map((booking) => {
                const videosSold = booking.droneVideos?.videosSold || 0;
                const videoPrice = booking.droneVideos?.videoPrice || 0;
                const total = videosSold * videoPrice;

                return (
                  <tr key={booking._id} className="hover:bg-gray-700/50">
                    <td className="px-3 py-3">
                      <div className="text-white font-medium text-sm">{booking.bookingId}</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-white text-sm">{booking.customerName}</div>
                      <div className="text-xs text-gray-400">{booking.whatsappNumber}</div>
                    </td>
                    <td className="px-3 py-3 text-white text-sm">{booking.locationName}</td>
                    <td className="px-3 py-3 text-white text-sm whitespace-nowrap">
                      {new Date(booking.bookingDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </td>
                    <td className="px-3 py-3 text-white text-center text-sm">{booking.numberOfJumpers}</td>
                    <td className="px-3 py-3 text-white text-center text-sm">
                      {videosSold > 0 ? videosSold : '-'}
                    </td>
                    <td className="px-3 py-3 text-white text-sm whitespace-nowrap">
                      {videoPrice > 0 ? `₹${videoPrice}` : '-'}
                    </td>
                    <td className="px-3 py-3 text-white font-medium text-sm whitespace-nowrap">
                      {total > 0 ? `₹${total.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex space-x-1 justify-center">
                        <button
                          onClick={() => handleEdit(booking)}
                          className="p-1.5 hover:bg-blue-500/20 rounded text-blue-400 transition-colors"
                          title="Edit Drone Sales"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleNoSale(booking._id)}
                          className="px-2 py-1 hover:bg-red-500/20 rounded text-red-400 text-xs transition-colors whitespace-nowrap"
                          title="No Sale"
                        >
                          No Sale
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {bookings.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400">No bookings found</p>
        </div>
      )}

      {/* Edit Drone Sales Modal */}
      {showEditModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-black text-white">Edit Drone Sales</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedBooking(null);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <XIcon size={24} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Booking Info */}
                <div className="bg-gray-700/50 rounded-lg p-4 space-y-2">
                  <p className="text-white"><span className="text-gray-400">Booking ID:</span> {selectedBooking.bookingId}</p>
                  <p className="text-white"><span className="text-gray-400">Customer:</span> {selectedBooking.customerName}</p>
                  <p className="text-white"><span className="text-gray-400">Number of Jumpers:</span> {selectedBooking.numberOfJumpers}</p>
                </div>

                {/* Videos Sold */}
                <div>
                  <label className="text-white font-medium mb-2 block">Number of Videos Sold *</label>
                  <input
                    type="number"
                    min="0"
                    max={selectedBooking.numberOfJumpers}
                    value={editData.videosSold}
                    onChange={(e) => setEditData({ ...editData, videosSold: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                  <p className="text-sm text-gray-400 mt-1">Maximum: {selectedBooking.numberOfJumpers}</p>
                </div>

                {/* Video Price */}
                <div>
                  <label className="text-white font-medium mb-2 block">Video Price (per video) *</label>
                  <input
                    type="number"
                    min="0"
                    value={editData.videoPrice}
                    onChange={(e) => setEditData({ ...editData, videoPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                </div>

                {/* Total */}
                <div>
                  <label className="text-white font-medium mb-2 block">Total Amount</label>
                  <input
                    type="text"
                    value={`₹${(editData.videosSold * editData.videoPrice).toLocaleString()}`}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg text-yellow-400 font-bold"
                  />
                </div>

                {/* Payment Type */}
                <div>
                  <label className="text-white font-medium mb-2 block">Payment Type *</label>
                  <select
                    value={editData.paymentType}
                    onChange={(e) => setEditData({ ...editData, paymentType: e.target.value, upiDetails: '' })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  >
                    <option value="cash">Cash</option>
                    <option value="UPI">UPI</option>
                  </select>
                </div>

                {/* UPI Details */}
                {editData.paymentType === 'UPI' && (
                  <div>
                    <label className="text-white font-medium mb-2 block">UPI Details *</label>
                    <input
                      type="text"
                      value={editData.upiDetails}
                      onChange={(e) => setEditData({ ...editData, upiDetails: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                      placeholder="Enter UPI transaction ID or details"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-4 pt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedBooking(null);
                  }}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsManager;
